/**
 * The door drawer's "Last Update" (twin slice 1).
 *
 * It used to read "3s ago": `Date.now()` minus the worker's
 * `updated_at_ms`. That subtracts one machine's clock from another's, so
 * any skew between the worker and this device made a dead door read
 * fresh or a live one read old -- the very thing the liveness contract
 * forbids (contract §2: an age is a device-clock RECEIPT age, never
 * `Date.now() - updated_at_ms`). This page keeps no receipt book for
 * doors, so it no longer states an age at all.
 *
 * Instead it gives the stamp's TIME OF DAY on the site's clock ("14:02",
 * or "14:02 (device time)" when the site declares no zone), and says
 * "not current" whenever that time could be mistaken for a live reading:
 *
 *   - nothing publishes state for the door (a registry-only row), or the
 *     stamp is unreadable: "not current", with no time;
 *   - the live feed is clamped (`feed-health.ts`): every record on the
 *     page is last-known, this one included;
 *   - the stamp is from an earlier (or later) calendar day on the site's
 *     clock than this device's today: a bare "14:02" would read as
 *     today's. The worker re-stamps a reporting door on every sample, so
 *     a reporting door's stamp is never a day old. The comparison is in
 *     whole days, so ordinary clock skew moves it only around midnight.
 *
 * Whether THIS door's stamp is still advancing, minute to minute, is not
 * established here (no door receipts); the health rows above it --
 * Controller, Last reading -- are the drawer's per-door freshness.
 */
import { clockText, dayClockText, type SiteTimeZone } from '../../state/attention/words';
import { browserTimeZone, isKnownTimeZone, zonedDayIndex } from '../Tasks/zoned-time';

export const NOT_CURRENT = 'not current';

export interface DoorLastUpdateInput {
  /** `DoorViewModel.lastSeen`: the worker's stamp, as an ISO string. */
  readonly lastSeen: string | undefined;
  readonly hasLiveState: boolean;
  /** `FeedHealth.clamped` for the door's site. */
  readonly feedClamped: boolean;
  /** This device's `Date.now()`, for which calendar day it is. */
  readonly nowMs: number;
  readonly clock: SiteTimeZone;
}

export interface DoorLastUpdate {
  /** False whenever the text must not be read as a live reading. */
  readonly current: boolean;
  readonly text: string;
}

export function doorLastUpdate(input: DoorLastUpdateInput): DoorLastUpdate {
  const stampMs = input.lastSeen ? Date.parse(input.lastSeen) : Number.NaN;
  if (!input.hasLiveState || !Number.isFinite(stampMs)) {
    return { current: false, text: NOT_CURRENT };
  }
  const zone = isKnownTimeZone(input.clock.timeZone) ? input.clock.timeZone : browserTimeZone();
  const today = zonedDayIndex(stampMs, zone) === zonedDayIndex(input.nowMs, zone);
  const time = clockText(stampMs, input.clock);
  if (today && !input.feedClamped) return { current: true, text: time };
  // Another day names the day as the lift drawer and Right now do
  // ("yesterday 23:58", "27 Sep 23:58"; copy deck v2, A5), on the same
  // clock and with the same label -- not a third format of its own.
  const at = today ? time : dayClockText(stampMs, input.clock, input.nowMs);
  return { current: false, text: `${NOT_CURRENT} · last ${at}` };
}
